
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { RankFrequency, TypeExamTestLevel } from '@/__generated__/graphql';
import { TestLevelsWithFilter } from '@/api/common';
import ModalInput from '@/components/common/Modal/components/ModalInput';
import RankingTestLevel from '@/components/RankingTestLevel';
import useBreakpointValue from '@/hooks/useBreakpointValue';
import { EXAM_US_URL } from '@/lib/routes';
import { useAuth } from '@/providers/auth';
import { useQuery } from '@apollo/client';
import ModalInfoProfileRanking from 'components/ModalInfoProfileRanking';
import RankingLocation from '@/components/RankingLocation';
import RankingSchool from '@/components/RankingSchool';
import { TabTable } from '@/components/TableRanking';
import { DinRoundProFont } from '@/lib/utils';
import { TextFit } from '@flyer/components';
import { BgImage } from '@flyer/components/next';
import bgHome from '@/assets/images/bg-home.jpg';
import bgBack from '@/assets/icons/btn_back.png';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import mapIcon from '@/assets/icons/ic-map.png';
import iconBag from '@/assets/icons/icon_bag.png';
import iconLevel from '@/assets/icons/test_level.png';
import bgTitle from '@/assets/images/bg-title.png';
import iconSearch from '@/assets/icons/icon-search.png';
import iconBackMobile from '@/assets/icons/icon-close.png';
import IconOpen from '@/assets/svg/ic-open.svg';
import { twMerge } from 'tailwind-merge';
import { QueryParamConfig, StringParam, useQueryParams, withDefault } from 'use-query-params';
const TableRanking = dynamic(() => import('@/components/TableRanking'), {
    ssr: false,
});
const TabTypeParam: QueryParamConfig<RankFrequency> = {
    encode: (value) => value,
    decode: (value) => {
        if (typeof value === 'string' &&
            [
                RankFrequency.Daily,
                RankFrequency.Weekly,
                RankFrequency.Monthly,
                RankFrequency.Yearly,
            ].includes(value as RankFrequency)) {
            return value as RankFrequency;
        }
        return RankFrequency.Daily;
    },
};
const TabTableTypeParam: QueryParamConfig<TabTable> = {
    encode: (value) => value,
    decode: (value) => {
        if (typeof value === 'string' && ['school', 'student'].includes(value)) {
            return value as TabTable;
        }
        return 'student';
    },
};
export default function Home() {
    const { windowWidth } = useBreakpointValue();
    const [searchValue, setSearchValue] = useState<string | undefined>(undefined);
    const { t } = useTranslation('common');
    const [isOpen, setIsOpen] = useState(false);
    const { currentLearnerProfile } = useAuth();
    const isFistInitProvince = useRef<boolean>(false);
    const isFistSelectSchool = useRef<boolean>(false);
    const [q, setQ] = useQueryParams({
        schoolId: StringParam,
        provinceId: StringParam,
        testLevelId: StringParam,
        challengeCode: StringParam,
        currentTab: withDefault(TabTypeParam, RankFrequency.Daily),
        tableTab: withDefault(TabTableTypeParam, 'student'),
        currentProfileId: StringParam,
        userName: StringParam,
    });
    const classifyString = useMemo(() => {
        if (!q.challengeCode) {
            return {
                isShowClassRanking: true,
                isShowChallengeRanking: false,
                isSearchUserName: false,
                isSearchProfileId: false,
            };
        }
        // is ChallengeCode: 7632383
        if (/^\d+$/.test(q.challengeCode)) {
            return {
                isShowClassRanking: false,
                isShowChallengeRanking: true,
                isSearchUserName: false,
                isSearchProfileId: false,
            };
        }
        // search  user id
        if (q.challengeCode?.toLowerCase()?.startsWith('fs')) {
            return {
                isSearchProfileId: true,
                isShowClassRanking: false,
                isShowChallengeRanking: false,
                isSearchUserName: false,
            };
        }
        if (q.challengeCode.startsWith('CL')) {
            return {
                isShowClassRanking: true,
                isShowChallengeRanking: false,
                isSearchUserName: false,
                isSearchProfileId: false,
            };
        }
        return {
            isSearchUserName: true,
            isShowClassRanking: false,
            isShowChallengeRanking: false,
            isSearchProfileId: false,
        };
    }, [q.challengeCode]);
    const { data: dataTestLevel } = useQuery(TestLevelsWithFilter, {
        variables: {
            filterBy: {
                typeTestLevel: [TypeExamTestLevel.Classic],
            },
        },
        fetchPolicy: 'cache-and-network',
        onCompleted: (res) => {
            const levelInit = res?.testLevels?.nodes?.find((lv) => lv.slug === 'starters');
            if (levelInit && !q.testLevelId) {
                setQ({ testLevelId: levelInit.id });
            }
        },
    });
    const setProvince = useCallback((value?: string, schoolIdProp?: string) => {
        setQ({ provinceId: value, schoolId: schoolIdProp });
    }, [setQ]);
    const handleDropdownChange = useCallback((value: string | undefined, id: string | undefined) => {
        switch (id) {
            case 'schoolId': {
                isFistSelectSchool.current = true;
                if (value === undefined) {
                    setQ({ schoolId: undefined });
                }
                else {
                    setQ({ schoolId: value });
                }
                break;
            }
            case 'provinceId': {
                isFistInitProvince.current = true;
                if (value === undefined) {
                    setQ({ schoolId: undefined, provinceId: undefined });
                }
                else {
                    setQ({ provinceId: value, schoolId: undefined });
                }
                break;
            }
            case 'testLevelId': {
                setQ({ testLevelId: value });
                break;
            }
            default: {
                break;
            }
        }
    }, [setQ]);
    const listTab = useMemo(() => [
        {
            title: t('ranking.daily'),
            key: RankFrequency.Daily,
        },
        {
            title: t('ranking.weekly'),
            key: RankFrequency.Weekly,
        },
        {
            title: t('ranking.monthly'),
            key: RankFrequency.Monthly,
        },
        {
            title: t('ranking.yearly'),
            key: RankFrequency.Yearly,
        },
    ], [t]);
    const handlerChangeTab = useCallback((tabProps: TabTable) => {
        setQ({
            tableTab: tabProps,
            challengeCode: undefined,
        });
    }, [setQ]);
    const handlerChangeClassOrChallengeCode = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value);
    }, []);
    const onViewDetailStudent = useCallback((profileId: string) => {
        setQ({ currentProfileId: profileId });
    }, [setQ]);
    const onCloseViewDetailStudent = useCallback(() => {
        setQ({ currentProfileId: undefined });
    }, [setQ]);
    const renderListTab = useMemo(() => {
        return listTab.map((item) => {
            const isActive = q.currentTab && item.key === q.currentTab;
            return (<div aria-hidden key={item.key} onClick={() => setQ({
                    currentTab: item.key,
                })} className={twMerge('h-[45px] 2xl:h-[50px] flex items-center px-4 2xl:px-6 cursor-pointer', isActive
                    ? 'border-solid border-[1px] border-[#A86AEC] rounded-[8px] [background:linear-gradient(180deg,#A456DF_0%,#6F3FBA_100%)] text-white'
                    : 'text-[#9F60E5]')}>
          <span className="text-base 2xl:text-lg font-bold uppercase">{item.title}</span>
        </div>);
        });
    }, [listTab, q.currentTab, setQ]);
    const renderBackImage = useMemo(() => {
        if (windowWidth <= 1536) {
            return (<Image alt="back" src={iconBackMobile} className="flex justify-center items-center max-w-[84px]"/>);
        }
        return (<BgImage wrapAs="button" alt="back" src={bgBack} wrapClassName="max-w-[250px]" className="top-[14%] left-[50%] right-[16.5%] bottom-[14%] flex justify-center text-white items-center">
        <TextFit lineHeight={1.5} fontSize={21} className="uppercase font-semibold">
          {t('label.back')}
        </TextFit>
      </BgImage>);
    }, [t, windowWidth]);
    // update query
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchValue === undefined)
                return;
            if (searchValue) {
                setQ({
                    challengeCode: searchValue,
                });
            }
            else {
                setQ({
                    challengeCode: undefined,
                });
            }
        }, 500);
        return () => {
            clearTimeout(timer);
        };
    }, [searchValue, setQ]);
    const renderLinkHome = useMemo(() => {
        return EXAM_US_URL;
    }, []);
    const isShowCurrentProfile = useMemo(() => {
        return !(q.challengeCode || q.userName || q.provinceId || q.schoolId);
    }, [q.challengeCode, q.provinceId, q.schoolId, q.userName]);
    // update init school
    useEffect(() => {
        if (currentLearnerProfile?.school?.province?.id &&
            !q.provinceId &&
            !isFistInitProvince.current) {
            isFistInitProvince.current = true;
            setQ({ provinceId: currentLearnerProfile.school?.province?.id });
        }
    }, [currentLearnerProfile?.school?.province?.id, q.provinceId, setQ]);
    const renderOptionFilter = useMemo(() => {
        const isDisable = q.tableTab === 'school';
        return (<>
        <div className={twMerge('2xl:w-[320px]', isDisable ? 'opacity-50' : 'opacity-100')}>
          <ModalInput disabled={q.tableTab === 'school'} inputPrefix={<Image src={iconSearch} alt="search" className="w-[45px] h-auto"/>} className="xl:text-[16px] z-[5]" labelClassName="xl:text-[12px]" wrapClassName="w-full" defaultValue={q.challengeCode || ''} onChange={handlerChangeClassOrChallengeCode} placeholder={t('label.placeholder_class_code')} label={t('label.label_class_code')} prefixClassName="sm:mr-3"/>
        </div>

        <div>
          <RankingTestLevel id="testLevelId" label={t('label.testLevel')} prefix={<Image src={iconLevel} alt="Location" className="max-w-[25px] xl:max-w-[28px]"/>} dataTestLevel={dataTestLevel} wrapClassName="z-[21]" selected={q.testLevelId} onChange={handleDropdownChange} hasSearchable={false} inputProps={{
                className: 'xl:text-[16px]',
                labelClassName: 'xl:text-[12px]',
            }}/>
        </div>
        <div>
          <RankingLocation id="provinceId" label={t('account:form.city.label')} prefix={<Image src={mapIcon} alt="Location" className="max-w-[25px] xl:max-w-[28px]"/>} wrapClassName="z-[21]" selected={q.provinceId} onChange={handleDropdownChange} inputProps={{
                className: 'xl:text-[16px]',
                labelClassName: 'xl:text-[12px]',
            }}/>
        </div>
        <div className={twMerge('xl:w-[320px]', isDisable ? 'opacity-50' : 'opacity-100')}>
          <RankingSchool id="schoolId" disabled={isDisable} label={t('account:form.school.label')} prefix={<Image src={iconBag} alt="School" className="max-w-[25px] xl:max-w-[30px]"/>} wrapClassName="z-[20]" selected={isDisable ? undefined : q.schoolId} city={q.provinceId} initSchoolId={q.schoolId && !isFistSelectSchool.current ? q.schoolId : undefined} onChange={handleDropdownChange} setProvince={setProvince} inputProps={{
                className: 'xl:text-[16px]',
                labelClassName: 'xl:text-[12px]',
            }}/>
        </div>
      </>);
    }, [
        dataTestLevel,
        handleDropdownChange,
        handlerChangeClassOrChallengeCode,
        q.challengeCode,
        q.provinceId,
        q.schoolId,
        q.tableTab,
        q.testLevelId,
        setProvince,
        t,
    ]);
    return (<BgImage src={bgHome} alt="ranking home" data-test="home-page" className="main-home flex flex-col select-none min-h-screen mobile-home overflow-hidden " wrapClassName={`h-screen overflow-hidden page-wrapper, ${DinRoundProFont.className}`} imageClassName="h-full object-cover w-full">
      <div id="wrap-header-desktop-ranking">
        <div className="mt-6 px-4 flex flex-row justify-between">
          <Link href={renderLinkHome}>{renderBackImage}</Link>
          <div className="lg:flex hidden-custom w-fit flex-row gap-2">{renderOptionFilter}</div>
          <div className="lg:hidden-custom flex">
            <div aria-hidden className="w-[54px]" onClick={() => setIsOpen(true)}>
              <IconOpen className="w-[54px]"/>
            </div>
          </div>
        </div>
        {isOpen && (<div aria-hidden className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setIsOpen(false)}/>)}
        <div className={`lg:hidden-custom fixed top-0 right-0 w-[80%] md:w-1/2 h-full bg-[#C375EE] shadow-lg z-50 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex flex-col h-full pt-8 p-4">
            <nav className="space-y-4">{renderOptionFilter}</nav>
          </div>
        </div>

        <div className="flex w-full justify-center items-center md:mt-4">
          <BgImage alt="flyer ranking" src={bgTitle} className="flex justify-center items-center" wrapClassName="max-w-[400px] 2xl:max-w-[500px]">
            <span style={{
            textShadow: '#C15F35 -1.5px -1.5px 0px,#C15F35 1px -2.5px 0px,#C15F35 0px 1.5px 0px',
        }} className="pt-4 text-[35px] 2xl:text-[45px] font-bold text-white uppercase">
              {t('ranking.title')}
            </span>
          </BgImage>
        </div>
        <div data-test-id="tab-ranking" className="flex w-full justify-center items-center mt-4 relative max-w-[1300px] m-auto">
          <div className="flex flex-row bg-[#4E1E84] border-solid border-[1px] border-[#A86AEC] rounded-[8px] p-1 gap-2 md:gap-6 ">
            {renderListTab}
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center h-full mt-4">
        <TableRanking handlerChangeTab={handlerChangeTab} currentTabActive={q.tableTab} isShowSchoolTable={q.tableTab && q.tableTab === 'school'} frequency={q.currentTab} testLevelId={q.testLevelId} isShowChallengeRanking={classifyString.isShowChallengeRanking} challengeClassUserCode={q.challengeCode} provinceId={q.provinceId} schoolId={q.schoolId} isShowClassRanking={classifyString.isShowClassRanking} isSearchUserName={classifyString.isSearchUserName} isSearchProfileId={classifyString.isSearchProfileId} onViewDetailStudent={onViewDetailStudent} isShowCurrentProfile={isShowCurrentProfile}/>
      </div>
      {q.currentProfileId && (<ModalInfoProfileRanking profileId={q.currentProfileId} open isMobile={windowWidth < 768} onClose={onCloseViewDetailStudent}/>)}
    </BgImage>);
}

    async function __Next_Translate__getStaticProps__1a047bfe2d2__(ctx) {
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/index',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || (() => Promise.resolve({})),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a047bfe2d2__ as getStaticProps }
  